<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { CategoryStoreGroupPayload } from "@/types/interface/category-store-group";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useCategoryGroupStore } from "@/stores/master/category-group-store";
import { useDepartmentStore } from "@/stores/master/department";
import { usePositionStore } from "@/stores/master/position";
import { mapOptionMultiselect } from "@/utils/helper";
import { useCategoryStore } from "@/stores/master/category-store";

const idCategoryStoreGroup = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const categoryStoreGroup = useCategoryGroupStore();
const departmentStore = useDepartmentStore();
const categoryStore = useCategoryStore();
const positionStore = usePositionStore();

const listDepartment = ref<any>();
const listCategory = ref<any>();
const listPosition = ref<any>();
const detailCategoryStoreGroup = ref<any>();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Category Store Name is required" }),
    category_code: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.string()
    ),
    department_code: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.string()
    ),
    pic: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: category_code } = useField<number>("category_code");
const { value: department_code } = useField<number>("department_code");
const { value: pic } = useField<number>("pic");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadCategoryStoreGroup: CategoryStoreGroupPayload = {
    name: name.value,
    category_code: category_code.value?.value,
    department_code: department_code.value?.value,
    pic: pic.value?.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await categoryStoreGroup.dispatchUpdate(
      idCategoryStoreGroup.value,
      payloadCategoryStoreGroup
    );
  } else {
    responseApi.value = await categoryStoreGroup.dispatchCreate(
      payloadCategoryStoreGroup
    );
  }
  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Category Group Store Successfully Updated"
          : "Category Group Store Successfully Added"
      );
      router.push("/master/category-store-group");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadListDepartment = async () => {
  const { success, message } = await departmentStore.dispatchGetDepartments();
  if (success) {
    listDepartment.value = mapOptionMultiselect(
      await departmentStore?.departments?.items,
      "code",
      "name"
    );
  }
};

const loadListCategory = async () => {
  const { success, message } = await categoryStore.dispatchGetCategories();
  if (success) {
    listCategory.value = mapOptionMultiselect(
      await categoryStore.categories.items,
      "code",
      "name"
    );
  }
};

const loadListPosition = async () => {
  const { success, message } = await positionStore.dispatchGetPositions(1,10,'&is_pagination=false');
  if (success) {
    listPosition.value = mapOptionMultiselect(
      await positionStore.positions.items,
      "id",
      "name"
    );
  }
};

const loadDetailCategoryStoreGroup = async () => {
  const { success, message } = await categoryStoreGroup.dispatchDetail(
    idCategoryStoreGroup.value
  );
  if (success) {
    detailCategoryStoreGroup.value = await categoryStoreGroup.categoryGroup
      .data;
    name.value = detailCategoryStoreGroup.value.name;
    category_code.value = mapOptionMultiselect(
      detailCategoryStoreGroup.value.category,
      "code",
      "name"
    );
    department_code.value = mapOptionMultiselect(
      detailCategoryStoreGroup.value.department,
      "code",
      "name"
    );
  }
};

loadListDepartment();
loadListCategory();
loadListPosition();

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idCategoryStoreGroup.value = router.currentRoute.value.params.id.toString();
    loadDetailCategoryStoreGroup();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Category Store Group</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/category-store-group"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Category Store Group
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="category_code"
                    class="form-label label text-secondary"
                    >Category Store Name
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="category_code"
                      :options="listCategory"
                      placeholder="Choose Category Store"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.category_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="department_code"
                    class="form-label label text-secondary"
                    >Department <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="department_code"
                      :options="listDepartment"
                      placeholder="Choose Department"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.department_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="position_id"
                    class="form-label label text-secondary"
                    >Person in Charge <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="pic"
                      :options="listPosition"
                      placeholder="Choose Person in Charge"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.pic }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
